/**
 * RiskDashboardPanel — risk pipeline visualization + recent verdicts.
 * Check names are rendered dynamically from each verdict's
 * checks_passed/checks_failed arrays (real risk_aggregator output), never
 * from a hardcoded list.
 */

import { useAgentsOverview, useAgentRiskVerdicts } from "@/sdk/hooks/use-agents";
import { useTerminalStore } from "@/stores/terminal-store";
import type { RiskVerdict } from "@/sdk/types/agents";

function VerdictRow({ verdict }: { verdict: RiskVerdict }) {
  const passed = Array.isArray(verdict.checks_passed) ? verdict.checks_passed : [];
  const failed = Array.isArray(verdict.checks_failed) ? verdict.checks_failed : [];

  return (
    <div className="border-b border-zinc-800/30 px-3 py-2">
      <div className="mb-1 flex items-center justify-between">
        <span
          className={`text-[10px] font-bold uppercase ${verdict.approved ? "text-emerald-400" : "text-red-400"}`}
        >
          {verdict.approved ? "Approved" : "Rejected"}
        </span>
        <span className="text-[10px] text-zinc-600">
          {new Date(verdict.created_at).toLocaleTimeString()}
        </span>
      </div>

      {/* Pipeline visualization — one segment per check that actually fired */}
      {passed.length + failed.length > 0 ? (
        <div className="mb-1.5 flex gap-0.5">
          {passed.map((check) => (
            <div
              key={`p:${check}`}
              title={`${check.replace(/_/g, " ")} — passed`}
              className="h-1.5 flex-1 rounded-full bg-emerald-500"
            />
          ))}
          {failed.map((check) => (
            <div
              key={`f:${check}`}
              title={`${check.replace(/_/g, " ")} — failed`}
              className="h-1.5 flex-1 rounded-full bg-red-500"
            />
          ))}
        </div>
      ) : (
        <div className="mb-1.5 text-[10px] text-zinc-600">No check detail recorded</div>
      )}

      <div className="flex items-center justify-between text-xs">
        <span className="text-zinc-500 truncate max-w-[150px]">{verdict.market_id}</span>
        <span className="text-zinc-400 font-mono">
          ${(verdict.original_size ?? 0).toFixed(0)} → ${(verdict.adjusted_size ?? 0).toFixed(0)}
        </span>
      </div>
      {verdict.reason && (
        <p className="mt-0.5 text-[10px] text-zinc-600 line-clamp-1">{verdict.reason}</p>
      )}
      {failed.length > 0 && (
        <p className="mt-0.5 text-[10px] text-red-400/80 line-clamp-1">
          Failed: {failed.map((c) => c.replace(/_/g, " ")).join(", ")}
        </p>
      )}
    </div>
  );
}

export function RiskDashboardPanel() {
  const { selectedAgentId, selectAgent } = useTerminalStore();
  const { data: overview, isError: overviewError } = useAgentsOverview();
  const agentIds = overview?.agents ? Object.keys(overview.agents) : [];
  const activeAgent = selectedAgentId ?? agentIds[0] ?? "";

  const { data, isLoading, isError: verdictsError } = useAgentRiskVerdicts(activeAgent, 30);
  const verdicts = data?.verdicts ?? [];

  const approvedCount = verdicts.filter((v) => v.approved).length;
  const rejectedCount = verdicts.length - approvedCount;

  // Legend derived from the checks that actually fired, in first-seen order.
  const seenChecks: string[] = [];
  for (const v of verdicts) {
    for (const check of [
      ...(Array.isArray(v.checks_passed) ? v.checks_passed : []),
      ...(Array.isArray(v.checks_failed) ? v.checks_failed : []),
    ]) {
      if (!seenChecks.includes(check)) seenChecks.push(check);
    }
  }

  return (
    <div className="flex h-full flex-col bg-zinc-950">
      {/* Summary header */}
      <div className="flex items-center justify-between border-b border-zinc-800 px-3 py-2">
        <div className="flex items-center gap-3 text-xs">
          <select
            value={activeAgent}
            onChange={(e) => selectAgent(e.target.value)}
            className="rounded border border-zinc-700 bg-zinc-900 px-2 py-0.5 text-xs text-zinc-300"
          >
            {agentIds.map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-emerald-400">{approvedCount} approved</span>
          <span className="text-red-400">{rejectedCount} rejected</span>
        </div>
      </div>

      {/* Risk check legend (real backend check names) */}
      {seenChecks.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-zinc-800/50 px-3 py-1">
          {seenChecks.map((check) => (
            <span key={check} className="text-[9px] text-zinc-600">
              {check.replace(/_/g, " ")}
            </span>
          ))}
        </div>
      )}

      {/* Verdicts list */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="mt-8 text-center text-xs text-zinc-500">Loading...</div>
        ) : verdicts.length === 0 ? (
          // A failed overview read disables the verdicts query entirely
          // (activeAgent=""), so both reads must be checked before claiming
          // "No verdicts yet" — an errored read is not an empty book.
          overviewError ? (
            <div className="mt-8 text-center text-xs text-amber-400">
              Agent data unavailable — backend unreachable
            </div>
          ) : verdictsError ? (
            <div className="mt-8 text-center text-xs text-amber-400">
              Risk verdicts unavailable — backend unreachable
            </div>
          ) : (
            <div className="mt-8 text-center text-xs text-zinc-600">No verdicts yet</div>
          )
        ) : (
          verdicts.map((v) => <VerdictRow key={v.id} verdict={v} />)
        )}
      </div>
    </div>
  );
}
